import * as Cesium from "cesium";

import { BasemapClipContour } from "./types";

interface ZoneAplatissementTerrain {
  id: string;
  contours: BasemapClipContour[];
  hauteurMetres: number;
}

interface ZoneAplatissementNormalisee {
  id: string;
  rectangles: Cesium.Rectangle[];
  contoursRadians: number[][][];
  hauteurMetres: number;
  margeExterneMetres: number;
}

const MAX_LEVEL_APLATISSEMENT = 18;
const EPSILON_HAUTEUR = 0.01;
const MARGE_EXTERNE_APLATISSEMENT_METRES = 1.5;

const scratchCartographic = new Cesium.Cartographic();
const scratchUv = new Cesium.Cartesian2();
const scratchPosition = new Cesium.Cartesian3();
const scratchNormal = new Cesium.Cartesian3();
const scratchGeodeticSurfaceNormal = new Cesium.Cartesian3();

function normaliserLongitude(longitude: number): number {
  return Cesium.Math.negativePiToPi(longitude);
}

function estPointDansAnneau(
  longitude: number,
  latitude: number,
  anneau: number[][],
): boolean {
  let interieur = false;
  const pointLongitude = normaliserLongitude(longitude);

  for (let i = 0, j = anneau.length - 1; i < anneau.length; j = i++) {
    const xi = normaliserLongitude(anneau[i][0]);
    const yi = anneau[i][1];
    const xj = normaliserLongitude(anneau[j][0]);
    const yj = anneau[j][1];

    const intersecte =
      yi > latitude !== yj > latitude &&
      pointLongitude < ((xj - xi) * (latitude - yi)) / (yj - yi) + xi;

    if (intersecte) {
      interieur = !interieur;
    }
  }

  return interieur;
}

function rectangleIntersectsZone(
  rectangle: Cesium.Rectangle,
  zone: ZoneAplatissementNormalisee,
): boolean {
  return zone.rectangles.some(
    (zoneRectangle) =>
      Cesium.Rectangle.simpleIntersection(rectangle, zoneRectangle) !==
      undefined,
  );
}

function calculerDistanceMetres(
  longitudeA: number,
  latitudeA: number,
  longitudeB: number,
  latitudeB: number,
): number {
  const geodesic = new Cesium.EllipsoidGeodesic(
    new Cesium.Cartographic(longitudeA, latitudeA),
    new Cesium.Cartographic(longitudeB, latitudeB),
  );
  return geodesic.surfaceDistance;
}

function distancePointSegmentMetres(
  longitude: number,
  latitude: number,
  debutLongitude: number,
  debutLatitude: number,
  finLongitude: number,
  finLatitude: number,
): number {
  const milieuLongitude = (debutLongitude + finLongitude) * 0.5;
  const milieuLatitude = (debutLatitude + finLatitude) * 0.5;
  const cosLatitude = Math.max(Math.cos(milieuLatitude), 1e-6);

  const ax = (debutLongitude - milieuLongitude) * cosLatitude;
  const ay = debutLatitude - milieuLatitude;
  const bx = (finLongitude - milieuLongitude) * cosLatitude;
  const by = finLatitude - milieuLatitude;
  const px = (longitude - milieuLongitude) * cosLatitude;
  const py = latitude - milieuLatitude;

  const abx = bx - ax;
  const aby = by - ay;
  const longueurCarree = abx * abx + aby * aby;
  const t =
    longueurCarree <= 1e-12
      ? 0
      : Cesium.Math.clamp(
          ((px - ax) * abx + (py - ay) * aby) / longueurCarree,
          0,
          1,
        );

  const projectionLongitude = (ax + abx * t) / cosLatitude + milieuLongitude;
  const projectionLatitude = ay + aby * t + milieuLatitude;

  return calculerDistanceMetres(
    longitude,
    latitude,
    projectionLongitude,
    projectionLatitude,
  );
}

function estDansMargeExterneZone(
  longitude: number,
  latitude: number,
  zone: ZoneAplatissementNormalisee,
): boolean {
  if (zone.margeExterneMetres <= 0) {
    return false;
  }

  for (const anneau of zone.contoursRadians) {
    if (anneau.length < 2) {
      continue;
    }

    for (let index = 0; index < anneau.length; index += 1) {
      const courant = anneau[index];
      const suivant = anneau[(index + 1) % anneau.length];
      const distance = distancePointSegmentMetres(
        longitude,
        latitude,
        courant[0],
        courant[1],
        suivant[0],
        suivant[1],
      );

      if (distance <= zone.margeExterneMetres) {
        return true;
      }
    }
  }

  return false;
}

function pointDansZone(
  longitude: number,
  latitude: number,
  zone: ZoneAplatissementNormalisee,
): boolean {
  for (let indexContour = 0; indexContour < zone.contoursRadians.length; indexContour += 1) {
    const anneau = zone.contoursRadians[indexContour];
    if (anneau.length < 3) {
      continue;
    }

    if (estPointDansAnneau(longitude, latitude, anneau)) {
      return true;
    }
  }

  return estDansMargeExterneZone(longitude, latitude, zone);
}

function collecterZonesAffectantRectangle(
  rectangle: Cesium.Rectangle,
  zones: ZoneAplatissementNormalisee[],
): ZoneAplatissementNormalisee[] {
  return zones.filter((zone) => rectangleIntersectsZone(rectangle, zone));
}

function calculerHauteurAplatiePourPoint(
  longitude: number,
  latitude: number,
  zones: ZoneAplatissementNormalisee[],
): number | undefined {
  for (let index = zones.length - 1; index >= 0; index -= 1) {
    const zone = zones[index];
    if (pointDansZone(longitude, latitude, zone)) {
      return zone.hauteurMetres;
    }
  }

  return undefined;
}

function lireRectangleTuile(
  provider: Cesium.TerrainProvider,
  x: number,
  y: number,
  level: number,
): Cesium.Rectangle {
  return provider.tilingScheme.tileXYToRectangle(x, y, level);
}

function agrandirRectangle(
  rectangle: Cesium.Rectangle,
  margeMetres: number,
): Cesium.Rectangle {
  if (margeMetres <= 0) {
    return Cesium.Rectangle.clone(rectangle);
  }

  const centreLatitude = (rectangle.south + rectangle.north) * 0.5;
  const deltaLatitude = margeMetres / Cesium.Ellipsoid.WGS84.maximumRadius;
  const deltaLongitude =
    margeMetres /
    (Cesium.Ellipsoid.WGS84.maximumRadius * Math.max(Math.cos(centreLatitude), 1e-6));

  return new Cesium.Rectangle(
    rectangle.west - deltaLongitude,
    rectangle.south - deltaLatitude,
    rectangle.east + deltaLongitude,
    rectangle.north + deltaLatitude,
  );
}

function normaliserContours(
  zones: ZoneAplatissementTerrain[],
): ZoneAplatissementNormalisee[] {
  return zones
    .map((zone) => {
      const contoursRadians = zone.contours
        .map((contour) => {
          const points = contour.coordinates
            .filter(
              (coordinate) =>
                Array.isArray(coordinate) &&
                coordinate.length >= 2 &&
                Number.isFinite(coordinate[0]) &&
                Number.isFinite(coordinate[1]),
            )
            .map(([longitude, latitude]) => [
              Cesium.Math.toRadians(longitude),
              Cesium.Math.toRadians(latitude),
            ]);

          if (points.length < 3) {
            return null;
          }

          return points;
        })
        .filter((points): points is number[][] => points !== null);

      if (contoursRadians.length === 0 || !Number.isFinite(zone.hauteurMetres)) {
        return null;
      }

      const margeExterneMetres = MARGE_EXTERNE_APLATISSEMENT_METRES;
      const rectangles = contoursRadians.map((anneau) => {
        const cartographics = anneau.map(([longitude, latitude]) =>
          new Cesium.Cartographic(longitude, latitude, 0),
        );
        return agrandirRectangle(
          Cesium.Rectangle.fromCartographicArray(cartographics),
          margeExterneMetres,
        );
      });

      return {
        id: zone.id,
        rectangles,
        contoursRadians,
        hauteurMetres: zone.hauteurMetres,
        margeExterneMetres,
      } satisfies ZoneAplatissementNormalisee;
    })
    .filter((zone): zone is ZoneAplatissementNormalisee => zone !== null);
}

function reconstruireBoundingSphereDepuisVertices(
  vertices: Float32Array,
  encoding: TerrainEncodingLike,
  vertexCountWithoutSkirts: number,
  result?: Cesium.BoundingSphere,
): Cesium.BoundingSphere {
  const positions = new Array<number>(vertexCountWithoutSkirts * 3);

  for (let index = 0; index < vertexCountWithoutSkirts; index += 1) {
    const position = encoding.decodePosition(vertices, index, scratchPosition);
    const offset = index * 3;
    positions[offset] = position.x;
    positions[offset + 1] = position.y;
    positions[offset + 2] = position.z;
  }

  return Cesium.BoundingSphere.fromVertices(positions, undefined, 3, result);
}

function mettreAJourOrientedBoundingBox(
  rectangle: Cesium.Rectangle,
  minimumHeight: number,
  maximumHeight: number,
  obb?: Cesium.OrientedBoundingBox,
): Cesium.OrientedBoundingBox {
  return Cesium.OrientedBoundingBox.fromRectangle(
    rectangle,
    minimumHeight,
    maximumHeight,
    Cesium.Ellipsoid.WGS84,
    obb,
  );
}

function recalculerHauteursMinMax(
  vertices: Float32Array,
  encoding: TerrainEncodingLike,
  vertexCountWithoutSkirts: number,
): { minimumHeight: number; maximumHeight: number } {
  let minimumHeight = Number.POSITIVE_INFINITY;
  let maximumHeight = Number.NEGATIVE_INFINITY;

  for (let index = 0; index < vertexCountWithoutSkirts; index += 1) {
    const height = encoding.decodeHeight(vertices, index);
    minimumHeight = Math.min(minimumHeight, height);
    maximumHeight = Math.max(maximumHeight, height);
  }

  if (!Number.isFinite(minimumHeight) || !Number.isFinite(maximumHeight)) {
    return { minimumHeight: 0, maximumHeight: 0 };
  }

  return { minimumHeight, maximumHeight };
}

type BufferHeightmap =
  | Int8Array
  | Uint8Array
  | Int16Array
  | Uint16Array
  | Int32Array
  | Uint32Array
  | Float32Array
  | Float64Array;

type TerrainEncodingLike = {
  hasWebMercatorT: boolean;
  hasVertexNormals: boolean;
  hasGeodeticSurfaceNormals: boolean;
  decodePosition: (
    buffer: Float32Array,
    index: number,
    result?: Cesium.Cartesian3,
  ) => Cesium.Cartesian3;
  decodeTextureCoordinates: (
    buffer: Float32Array,
    index: number,
    result?: Cesium.Cartesian2,
  ) => Cesium.Cartesian2;
  decodeHeight: (buffer: Float32Array, index: number) => number;
  decodeWebMercatorT: (buffer: Float32Array, index: number) => number;
  decodeNormal: (
    buffer: Float32Array,
    index: number,
    result: Cesium.Cartesian3,
  ) => Cesium.Cartesian3;
  decodeGeodeticSurfaceNormal: (
    buffer: Float32Array,
    index: number,
    result: Cesium.Cartesian3,
  ) => Cesium.Cartesian3;
  encode: (
    vertexBuffer: Float32Array,
    bufferIndex: number,
    position: Cesium.Cartesian3,
    uv: Cesium.Cartesian2,
    height: number,
    normalToPack?: Cesium.Cartesian3,
    webMercatorT?: number,
    geodeticSurfaceNormal?: Cesium.Cartesian3,
  ) => void;
};

type TerrainMeshLike = {
  vertices: Float32Array;
  stride: number;
  vertexCountWithoutSkirts: number;
  minimumHeight: number;
  maximumHeight: number;
  rectangle: Cesium.Rectangle;
  boundingSphere3D: Cesium.BoundingSphere;
  orientedBoundingBox?: Cesium.OrientedBoundingBox;
  occludeePointInScaledSpace: Cesium.Cartesian3;
  encoding: TerrainEncodingLike;
  _lastPickSceneMode?: number;
  _terrainPicker?: { needsRebuild?: boolean };
};

type OptionsCreateMeshTerrainQuantized = {
  tilingScheme: Cesium.TilingScheme;
  x: number;
  y: number;
  level: number;
  exaggeration?: number;
  exaggerationRelativeHeight?: number;
  throttle?: boolean;
};

type QuantizedMeshTerrainDataAvecCreateMesh = Cesium.QuantizedMeshTerrainData & {
  createMesh: (
    options: OptionsCreateMeshTerrainQuantized,
  ) => Promise<TerrainMeshLike> | undefined;
};

type StructureHeightmap = {
  heightScale?: number;
  heightOffset?: number;
  elementsPerHeight?: number;
  stride?: number;
  elementMultiplier?: number;
  isBigEndian?: boolean;
  lowestEncodedHeight?: number;
  highestEncodedHeight?: number;
};

function clonerBufferHeightmap(source: BufferHeightmap): BufferHeightmap {
  if (source instanceof Int8Array) {
    return new Int8Array(source);
  }
  if (source instanceof Uint8Array) {
    return new Uint8Array(source);
  }
  if (source instanceof Int16Array) {
    return new Int16Array(source);
  }
  if (source instanceof Uint16Array) {
    return new Uint16Array(source);
  }
  if (source instanceof Int32Array) {
    return new Int32Array(source);
  }
  if (source instanceof Uint32Array) {
    return new Uint32Array(source);
  }
  if (source instanceof Float32Array) {
    return new Float32Array(source);
  }

  return new Float64Array(source);
}

function clonerHeightmapTerrainData(
  source: Cesium.HeightmapTerrainData,
): Cesium.HeightmapTerrainData {
  const sourceAsAny = source as unknown as {
    _buffer: BufferHeightmap;
    _width: number;
    _height: number;
    _childTileMask: number;
    _encoding: number;
    _structure: StructureHeightmap;
    _createdByUpsampling: boolean;
    _waterMask?: Cesium.TerrainData["waterMask"];
  };

  const buffer = clonerBufferHeightmap(sourceAsAny._buffer);
  const waterMask = sourceAsAny._waterMask;

  return new Cesium.HeightmapTerrainData({
    buffer,
    width: sourceAsAny._width,
    height: sourceAsAny._height,
    childTileMask: sourceAsAny._childTileMask,
    encoding: sourceAsAny._encoding,
    structure: { ...sourceAsAny._structure },
    createdByUpsampling: sourceAsAny._createdByUpsampling,
    waterMask: waterMask instanceof Uint8Array ? waterMask : undefined,
  });
}

function ecrireHauteurHeightmap(
  buffer: BufferHeightmap,
  structure: StructureHeightmap,
  index: number,
  height: number,
): void {
  const stride = structure.stride ?? 1;
  const elementsPerHeight = structure.elementsPerHeight ?? 1;
  const elementMultiplier = structure.elementMultiplier ?? 256;
  const isBigEndian = Boolean(structure.isBigEndian);
  const rawHeight = Math.round(
    (height - (structure.heightOffset ?? 0)) / (structure.heightScale ?? 1),
  );
  const clampedRawHeight = Math.max(0, rawHeight);
  const bufferIndex = index * stride;

  if (elementsPerHeight === 1) {
    buffer[bufferIndex] = clampedRawHeight;
    return;
  }

  let value = clampedRawHeight;
  const digits = new Array<number>(elementsPerHeight).fill(0);

  for (let digitIndex = elementsPerHeight - 1; digitIndex >= 0; digitIndex -= 1) {
    digits[digitIndex] = value % elementMultiplier;
    value = Math.floor(value / elementMultiplier);
  }

  for (let digitIndex = 0; digitIndex < elementsPerHeight; digitIndex += 1) {
    const targetIndex = isBigEndian
      ? bufferIndex + digitIndex
      : bufferIndex + (elementsPerHeight - 1 - digitIndex);
    buffer[targetIndex] = digits[digitIndex];
  }
}

function aplatirHeightmapTerrainData(
  terrainData: Cesium.HeightmapTerrainData,
  tilingScheme: Cesium.TilingScheme,
  x: number,
  y: number,
  level: number,
  zones: ZoneAplatissementNormalisee[],
): Cesium.HeightmapTerrainData {
  const terrainClone = clonerHeightmapTerrainData(terrainData);
  const terrainAsAny = terrainClone as unknown as {
    _buffer: BufferHeightmap;
    _width: number;
    _height: number;
    _structure: StructureHeightmap;
    _mesh?: unknown;
  };

  const rectangle = tilingScheme.tileXYToRectangle(x, y, level);
  const width = terrainAsAny._width;
  const height = terrainAsAny._height;

  for (let row = 0; row < height; row += 1) {
    const latitude = Cesium.Math.lerp(
      rectangle.north,
      rectangle.south,
      row / Math.max(height - 1, 1),
    );

    for (let column = 0; column < width; column += 1) {
      const longitude = Cesium.Math.lerp(
        rectangle.west,
        rectangle.east,
        column / Math.max(width - 1, 1),
      );
      const hauteurAplatie = calculerHauteurAplatiePourPoint(
        longitude,
        latitude,
        zones,
      );

      if (hauteurAplatie === undefined) {
        continue;
      }

      ecrireHauteurHeightmap(
        terrainAsAny._buffer,
        terrainAsAny._structure,
        row * width + column,
        hauteurAplatie,
      );
    }
  }

  terrainAsAny._mesh = undefined;
  return terrainClone;
}

function reencoderVertexTerrainQuantized(
  vertices: Float32Array,
  mesh: TerrainMeshLike,
  index: number,
  nouvelleHauteur: number,
): void {
  const encoding = mesh.encoding;
  const stride = mesh.stride;
  const position = encoding.decodePosition(vertices, index, scratchPosition);
  const uv = encoding.decodeTextureCoordinates(vertices, index, scratchUv);
  const webMercatorT = encoding.hasWebMercatorT
    ? encoding.decodeWebMercatorT(vertices, index)
    : undefined;
  const normal = encoding.hasVertexNormals
    ? encoding.decodeNormal(vertices, index, scratchNormal)
    : undefined;
  const geodeticSurfaceNormal = encoding.hasGeodeticSurfaceNormals
    ? encoding.decodeGeodeticSurfaceNormal(
        vertices,
        index,
        scratchGeodeticSurfaceNormal,
      )
    : undefined;

  const cartographic = Cesium.Cartographic.fromCartesian(
    position,
    Cesium.Ellipsoid.WGS84,
    scratchCartographic,
  );

  cartographic.height = nouvelleHauteur;
  const nouvellePosition = Cesium.Cartesian3.fromRadians(
    cartographic.longitude,
    cartographic.latitude,
    cartographic.height,
    Cesium.Ellipsoid.WGS84,
    position,
  );

  encoding.encode(
    vertices,
    index * stride,
    nouvellePosition,
    uv,
    nouvelleHauteur,
    normal,
    webMercatorT,
    geodeticSurfaceNormal,
  );
}

function aplatirTerrainMesh(
  mesh: TerrainMeshLike,
  zones: ZoneAplatissementNormalisee[],
): boolean {
  const vertexCount = mesh.vertexCountWithoutSkirts;
  const vertices = mesh.vertices;
  let aEteModifie = false;

  for (let index = 0; index < vertexCount; index += 1) {
    const position = mesh.encoding.decodePosition(vertices, index, scratchPosition);
    const cartographic = Cesium.Cartographic.fromCartesian(
      position,
      Cesium.Ellipsoid.WGS84,
      scratchCartographic,
    );

    if (!cartographic) {
      continue;
    }

    const hauteurAplatie = calculerHauteurAplatiePourPoint(
      cartographic.longitude,
      cartographic.latitude,
      zones,
    );

    if (hauteurAplatie === undefined) {
      continue;
    }

    const hauteurActuelle = mesh.encoding.decodeHeight(vertices, index);
    if (Math.abs(hauteurActuelle - hauteurAplatie) <= EPSILON_HAUTEUR) {
      continue;
    }

    reencoderVertexTerrainQuantized(vertices, mesh, index, hauteurAplatie);
    aEteModifie = true;
  }

  if (!aEteModifie) {
    return false;
  }

  const { minimumHeight, maximumHeight } = recalculerHauteursMinMax(
    vertices,
    mesh.encoding,
    vertexCount,
  );
  mesh.minimumHeight = minimumHeight;
  mesh.maximumHeight = maximumHeight;
  mesh.boundingSphere3D = reconstruireBoundingSphereDepuisVertices(
    vertices,
    mesh.encoding,
    vertexCount,
    mesh.boundingSphere3D,
  );
  mesh.orientedBoundingBox = mettreAJourOrientedBoundingBox(
    mesh.rectangle,
    minimumHeight,
    maximumHeight,
    mesh.orientedBoundingBox,
  );
  mesh.occludeePointInScaledSpace = Cesium.Cartesian3.clone(
    mesh.boundingSphere3D.center,
    mesh.occludeePointInScaledSpace,
  );
  (mesh as unknown as { _lastPickSceneMode?: number })._lastPickSceneMode = undefined;
  const terrainPicker = (mesh as unknown as { _terrainPicker?: { needsRebuild?: boolean } })._terrainPicker;
  if (terrainPicker) {
    terrainPicker.needsRebuild = true;
  }

  return true;
}

function envelopperTerrainData(
  terrainData: Cesium.TerrainData,
  tilingScheme: Cesium.TilingScheme,
  x: number,
  y: number,
  level: number,
  zones: ZoneAplatissementNormalisee[],
): Cesium.TerrainData {
  if (terrainData instanceof Cesium.HeightmapTerrainData) {
    return aplatirHeightmapTerrainData(terrainData, tilingScheme, x, y, level, zones);
  }

  if (terrainData instanceof Cesium.QuantizedMeshTerrainData) {
    const terrainQuantized = terrainData as QuantizedMeshTerrainDataAvecCreateMesh;
    const originalCreateMesh = terrainQuantized.createMesh.bind(terrainQuantized);
    const originalUpsample = terrainData.upsample.bind(terrainData);
    let cacheMeshAplati: Promise<TerrainMeshLike> | undefined;

    terrainQuantized.createMesh = (options) => {
      if (cacheMeshAplati) {
        return cacheMeshAplati;
      }

      const meshPromise = originalCreateMesh(options);
      if (!meshPromise) {
        return meshPromise;
      }

      cacheMeshAplati = Promise.resolve(meshPromise).then((mesh) => {
        aplatirTerrainMesh(mesh, zones);
        return mesh;
      });

      return cacheMeshAplati;
    };

    terrainData.upsample = (
      descendantTilingScheme,
      thisX,
      thisY,
      thisLevel,
      descendantX,
      descendantY,
      descendantLevel,
    ) => {
      const upsampled = originalUpsample(
        descendantTilingScheme,
        thisX,
        thisY,
        thisLevel,
        descendantX,
        descendantY,
        descendantLevel,
      );

      if (!upsampled) {
        return upsampled;
      }

      return Promise.resolve(upsampled).then((terrainEnfant) => {
        const rectangleEnfant = descendantTilingScheme.tileXYToRectangle(
          descendantX,
          descendantY,
          descendantLevel,
        );
        const zonesEnfant = collecterZonesAffectantRectangle(rectangleEnfant, zones);
        if (zonesEnfant.length === 0) {
          return terrainEnfant;
        }

        return envelopperTerrainData(
          terrainEnfant,
          descendantTilingScheme,
          descendantX,
          descendantY,
          descendantLevel,
          zonesEnfant,
        ) as Cesium.QuantizedMeshTerrainData;
      });
    };

    const originalInterpolateHeight = terrainData.interpolateHeight.bind(terrainData);
    terrainData.interpolateHeight = (interpolateRectangle, longitude, latitude) => {
      const hauteurAplatie = calculerHauteurAplatiePourPoint(longitude, latitude, zones);
      if (hauteurAplatie !== undefined) {
        return hauteurAplatie;
      }

      return originalInterpolateHeight(interpolateRectangle, longitude, latitude);
    };
  }

  return terrainData;
}

export class LocalizedFlattenTerrainProvider implements Cesium.TerrainProvider {
  private readonly provider: Cesium.TerrainProvider;
  private zones: ZoneAplatissementNormalisee[] = [];

  constructor(provider: Cesium.TerrainProvider) {
    this.provider = provider;
  }

  get errorEvent(): Cesium.Event {
    return this.provider.errorEvent;
  }

  get credit(): Cesium.Credit {
    return this.provider.credit ?? new Cesium.Credit("");
  }

  get tilingScheme(): Cesium.TilingScheme {
    return this.provider.tilingScheme;
  }

  get hasWaterMask(): boolean {
    return this.provider.hasWaterMask;
  }

  get hasVertexNormals(): boolean {
    return this.provider.hasVertexNormals;
  }

  get availability(): Cesium.TileAvailability | undefined {
    return this.provider.availability;
  }

  setFlattenZones(zones: ZoneAplatissementTerrain[]): void {
    this.zones = normaliserContours(zones);
  }

  getLevelMaximumGeometricError(level: number): number {
    return this.provider.getLevelMaximumGeometricError(level);
  }

  getTileDataAvailable(x: number, y: number, level: number): boolean | undefined {
    return this.provider.getTileDataAvailable(x, y, level);
  }

  loadTileDataAvailability(x: number, y: number, level: number): Promise<void> | undefined {
    return this.provider.loadTileDataAvailability(x, y, level);
  }

  requestTileGeometry(
    x: number,
    y: number,
    level: number,
    request?: Cesium.Request,
  ): Promise<Cesium.TerrainData> | undefined {
    const requested = this.provider.requestTileGeometry(x, y, level, request);
    if (!requested) {
      return requested;
    }

    return Promise.resolve(requested).then((terrainData) => {
      if (this.zones.length === 0 || level > MAX_LEVEL_APLATISSEMENT) {
        return terrainData;
      }

      const rectangle = lireRectangleTuile(this.provider, x, y, level);
      const zonesAffectantes = collecterZonesAffectantRectangle(rectangle, this.zones);
      if (zonesAffectantes.length === 0) {
        return terrainData;
      }

      return envelopperTerrainData(
        terrainData,
        this.provider.tilingScheme,
        x,
        y,
        level,
        zonesAffectantes,
      );
    });
  }
}

export type { ZoneAplatissementTerrain };
